import { useParams } from 'react-router-dom'
import Header from '../header'
import {
    useGetDevice,
    useGetLocationDevice,
} from '../../../../api/routes/devices'
import { useGetGeofences } from '../../../../api/routes/geofences'
import { useEffect, useRef, useState } from 'react'
import L from 'leaflet'
import moment from 'moment'
import {
    FeatureGroup,
    MapContainer,
    Marker,
    Polyline,
    Popup,
    TileLayer,
} from 'react-leaflet'
import MarkerClusterGroup from 'react-leaflet-markercluster'
import { Fullscreen, FullscreenExit } from '@mui/icons-material'
import { MapPin } from 'lucide-react'
import GeofenceMapLayer from './geofence-map-layer'
import GeofencePanel from './geofence-panel'

const createClusterCustomIcon = (cluster: any) => {
    const count = cluster.getChildCount()
    return L.divIcon({
        html: `<div style="
            display: flex;
            align-items: center;
            justify-content: center;
            width: 40px;
            height: 40px;
            border-radius: 50%;
            background-color: rgba(0, 123, 255, 0.8);
            color: white;
            font-size: 14px;
            font-weight: bold;
            border: 2px solid #ffffff;
        ">${count}</div>`,
        className: '',
        iconSize: [40, 40],
    })
}

const SingleDeviceLocationRoute = () => {
    const { id: deviceId } = useParams()
    const [dates, setDates] = useState({
        from: '',
        to: new Date().toISOString().split('T')[0],
    })
    const [drawingMode, setDrawingMode] = useState<'circle' | 'polygon' | null>(
        null
    )
    const [drawnGeofence, setDrawnGeofence] = useState<any>(null)
    const [map, setMap] = useState<any>(null)
    const [isFullScreen, setIsFullScreen] = useState(false)
    const featureGroupRef = useRef<any>()

    const { data: device, isLoading: isDeviceDataLoading } = useGetDevice(
        deviceId as string
    )
    const {
        data: gpsData,
        isLoading: isGpsDataLoading,
        refetch,
    } = useGetLocationDevice(deviceId || '', dates.from, dates.to)
    const { data: geofences } = useGetGeofences(deviceId || '')

    useEffect(() => {
        refetch()
    }, [dates])

    useEffect(() => {
        if (!map) return
        if (!gpsData || gpsData.length <= 1) return
        if (featureGroupRef.current) {
            map.fitBounds(featureGroupRef.current.getBounds())
        }
    }, [map])

    useEffect(() => {
        const handleFullScreenChange = () => {
            setIsFullScreen(!!document.fullscreenElement)
        }
        document.addEventListener('fullscreenchange', handleFullScreenChange)
        return () =>
            document.removeEventListener(
                'fullscreenchange',
                handleFullScreenChange
            )
    }, [])

    if (isDeviceDataLoading || isGpsDataLoading) {
        return (
            <div className="mx-auto py-6 lg:py-10 px-4 sm:px-6 lg:px-8 xl:px-32">
                <div className="animate-pulse space-y-6">
                    <div className="h-8 w-48 bg-muted rounded" />
                    <div className="h-10 w-full sm:w-96 bg-muted rounded-lg" />
                    <div className="h-80 bg-muted rounded-lg" />
                </div>
            </div>
        )
    }

    if (!device || !deviceId) {
        return (
            <div className="mx-auto py-6 lg:py-10 px-4 sm:px-6 lg:px-8 xl:px-32">
                <div className="rounded-lg border border-dashed p-12 text-center">
                    <p className="text-muted-foreground">Device not found</p>
                </div>
            </div>
        )
    }

    const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target
        setDates((prev) => ({ ...prev, [name]: value }))
    }

    const mappedData = (gpsData || []).map(
        (item: {
            id: string
            latitude: string
            longitude: string
            altitude: string
            timestamp: string
        }) => ({
            timestamp: parseInt(item.timestamp),
            value: {
                latitude: item.latitude,
                longitude: item.longitude,
            },
        })
    )

    const hasLocationData = mappedData.length > 0
    const lastPoint = hasLocationData ? mappedData[mappedData.length - 1] : null
    const position: [number, number] | null = lastPoint
        ? [
              parseFloat(lastPoint.value.latitude),
              parseFloat(lastPoint.value.longitude),
          ]
        : null

    const polylines = mappedData.map((item: any, index: number) => {
        if (index === 0) return null
        const prev = mappedData[index - 1]
        return (
            <Polyline
                key={`polyline-${index}`}
                positions={[
                    [
                        parseFloat(prev.value.latitude),
                        parseFloat(prev.value.longitude),
                    ],
                    [
                        parseFloat(item.value.latitude),
                        parseFloat(item.value.longitude),
                    ],
                ]}
            />
        )
    })

    const markers = mappedData.map((item: any, index: number) => (
        <Marker
            key={`marker-${index}`}
            position={[
                parseFloat(item.value.latitude),
                parseFloat(item.value.longitude),
            ]}
        >
            <Popup>
                {device.name} was seen at {moment(item.timestamp).format('LLL')}
            </Popup>
        </Marker>
    ))

    return (
        <div className="mx-auto py-6 lg:py-10 px-4 sm:px-6 lg:px-8 xl:px-32">
            <Header
                deviceId={deviceId}
                name={device?.name}
                businessId={device?.business?.id}
            />

            <div className="rounded-lg border bg-card mb-6">
                <div className="px-6 py-4 border-b">
                    <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                        Device Location
                    </h3>
                </div>
                <div className="p-6">
                    <div className="rounded-lg border overflow-hidden mb-6">
                        {hasLocationData && position ? (
                            <div className="rounded overflow-hidden">
                                <div id="map">
                                    <MapContainer
                                        center={position}
                                        zoom={17}
                                        scrollWheelZoom={true}
                                        whenReady={(mapInstance) =>
                                            setMap(mapInstance.target)
                                        }
                                        doubleClickZoom={!drawingMode}
                                    >
                                        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                                        <FeatureGroup ref={featureGroupRef}>
                                            {polylines}
                                            <MarkerClusterGroup
                                                animate={true}
                                                showCoverageOnHover={true}
                                                iconCreateFunction={
                                                    createClusterCustomIcon
                                                }
                                            >
                                                {markers}
                                            </MarkerClusterGroup>
                                        </FeatureGroup>

                                        <Marker position={position}>
                                            <Popup>
                                                {device.name} last seen at{' '}
                                                {moment(
                                                    lastPoint.timestamp
                                                ).format('LLL')}
                                            </Popup>
                                        </Marker>

                                        <GeofenceMapLayer
                                            geofences={geofences || []}
                                            drawingMode={drawingMode}
                                            onGeofenceDrawn={(geofence) => {
                                                setDrawnGeofence(geofence)
                                                setDrawingMode(null)
                                            }}
                                            onCancelDraw={() =>
                                                setDrawingMode(null)
                                            }
                                        />

                                        <div
                                            className="absolute top-2 right-2 z-10"
                                            style={{ zIndex: 1000 }}
                                        >
                                            <button
                                                className="p-2 bg-white rounded-full shadow hover:bg-gray-100 transition"
                                                onClick={() => {
                                                    if (!isFullScreen) {
                                                        map?.getContainer().requestFullscreen()
                                                    } else {
                                                        document.exitFullscreen()
                                                    }
                                                }}
                                                aria-label={
                                                    isFullScreen
                                                        ? 'Exit Fullscreen'
                                                        : 'Enter Fullscreen'
                                                }
                                            >
                                                {isFullScreen ? (
                                                    <FullscreenExit className="text-gray-700" />
                                                ) : (
                                                    <Fullscreen className="text-gray-700" />
                                                )}
                                            </button>
                                        </div>

                                        <div
                                            className="absolute top-16 right-2 z-10"
                                            style={{ zIndex: 1000 }}
                                        >
                                            <button
                                                className="p-2 bg-white rounded-full shadow hover:bg-gray-100 transition"
                                                onClick={() => {
                                                    if (map) {
                                                        map.setView(
                                                            position,
                                                            17
                                                        )
                                                    }
                                                }}
                                                aria-label="Recenter Map"
                                            >
                                                <MapPin className="text-gray-700" />
                                            </button>
                                        </div>
                                    </MapContainer>
                                </div>
                            </div>
                        ) : (
                            <div className="flex items-center justify-center h-96 bg-white border">
                                No location data
                            </div>
                        )}
                    </div>

                    <div>
                        <h4 className="text-sm font-medium text-foreground mb-4">
                            Date Range
                        </h4>
                        <div className="flex flex-col sm:flex-row gap-4">
                            <div className="space-y-1.5">
                                <label
                                    htmlFor="from"
                                    className="text-xs font-medium text-muted-foreground uppercase tracking-wide"
                                >
                                    From
                                </label>
                                <input
                                    type="date"
                                    name="from"
                                    id="from"
                                    value={dates.from}
                                    onChange={handleDateChange}
                                    className="block w-full sm:w-auto px-3 py-2 text-sm border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <label
                                    htmlFor="to"
                                    className="text-xs font-medium text-muted-foreground uppercase tracking-wide"
                                >
                                    To
                                </label>
                                <input
                                    type="date"
                                    name="to"
                                    id="to"
                                    value={dates.to}
                                    onChange={handleDateChange}
                                    className="block w-full sm:w-auto px-3 py-2 text-sm border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <GeofencePanel
                deviceId={deviceId}
                onStartDraw={(mode) => setDrawingMode(mode)}
                drawnGeofence={drawnGeofence}
                onClearDrawn={() => setDrawnGeofence(null)}
            />
        </div>
    )
}

export { SingleDeviceLocationRoute }
